import { useState, useEffect } from "react";

import bannerImg from "./images/cst-banner.jpg";
import logoImg from "./images/cst-logo.jpg";
import gallery1Img from "./images/gallery1.png";
import gallery2Img from "./images/gallery2.png";

const services = [
  { icon: "💻", title: "Laptop Repair", desc: "All brands — HP, Dell, Lenovo, Acer, Apple & more. Screen, keyboard, battery, motherboard." },
  { icon: "🖥", title: "Desktop Repair", desc: "Full desktop servicing, RAM upgrades, HDD/SSD replacements, OS installation." },
  { icon: "📟", title: "Projector Repair", desc: "Lamp replacement, lens cleaning, board-level repair for all projector brands." },
  { icon: "🖨", title: "Printer Repair", desc: "Inkjet & laser printer servicing, cartridge issues, network setup." },
  { icon: "📱", title: "Tablet Repair", desc: "Screen replacement, charging port fix, software issues for all tablet brands." },
  { icon: "🔧", title: "Chip Level Repair", desc: "Expert motherboard & chip-level diagnostics and repair — our specialty." },
];

const whyUs = [
  { icon: "🏆", point: "10+ Years Experience" },
  { icon: "⚡", point: "Fast Turnaround" },
  { icon: "🔍", point: "Free Diagnosis" },
  { icon: "✅", point: "Genuine Spare Parts" },
  { icon: "📦", point: "Doorstep Pickup & Drop" },
  { icon: "💰", point: "Affordable Pricing" },
];
const API_BASE = "https://computerservicetechnologies.onrender.com/api";
const SERVICE_REQUEST_API = `${API_BASE}/service-request`;
const ADMIN_API = `${API_BASE}/admin/requests`;

function validateForm(data, isPopup = false) {
  const errors = {};

  if (!data.name.trim()) {
    errors.name = "Please enter the customer's name.";
  } else if (data.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters.";
  }

  const phoneField = isPopup ? data.mobile : data.phone;
  if (!phoneField.trim()) {
    errors.phone = "Please enter a mobile number.";
  } else if (!/^[6-9]\d{9}$/.test(phoneField.trim())) {
    errors.phone = "Enter a valid 10-digit mobile number.";
  }

  if (!data.customerEmail.trim()) {
    errors.customerEmail = "Please enter customer email.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.customerEmail.trim())) {
    errors.customerEmail = "Enter a valid email address.";
  }

  if (isPopup && !data.repairFor) {
    errors.repairFor = "Please select what needs repair.";
  }

  if (!isPopup && !data.issue.trim()) {
    errors.issue = "Please describe your issue.";
  }

  if (Object.keys(errors).length > 0) {
    console.log("Form validation failed:", errors);
  } else {
    console.log("Form validation passed for:", isPopup ? "popup form" : "contact form");
  }

  return errors;
}

const inputStyle = (hasError) => ({
  width: "100%",
  padding: "11px 14px",
  borderRadius: 8,
  border: `1.5px solid ${hasError ? "#ef4444" : "#e2e8f0"}`,
  fontSize: 15,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  background: "#fff",
  color: "#1a1a2e",
});

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#1a1a2e",
  marginBottom: 6,
};

const errorTextStyle = {
  color: "#ef4444",
  fontSize: 12,
  fontWeight: 600,
  marginTop: 5,
};

export default function App() {
  const [view, setView] = useState("home"); // home | admin

  // ---- Contact Form ----
  const [formData, setFormData] = useState({ name: "", phone: "", issue: "", customerEmail: "" });
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ---- Popup ----
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState({ name: "", mobile: "", customerEmail: "", repairFor: "", issueDetails: "" });
  const [popupErrors, setPopupErrors] = useState({});
  const [popupSubmitted, setPopupSubmitted] = useState(false);
  const [popupSubmitting, setPopupSubmitting] = useState(false);

  // ---- Admin ----
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, resolved: 0 });
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    console.log("handleSubmit: contact form submit clicked", formData);
    const errors = validateForm(formData, false);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.log("handleSubmit: aborting submit due to validation errors", errors);
      return;
    }

    setFormSubmitting(true);
    const payload = {
      customerName: formData.name,
      phoneNumber: formData.phone,
      customerEmail: formData.customerEmail,
      issueDescription: formData.issue,
    };
    console.log("handleSubmit: sending POST to", SERVICE_REQUEST_API, "payload:", payload);

    try {
      const response = await fetch(SERVICE_REQUEST_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log("handleSubmit: response status", response.status, response.ok);

      if (response.ok) {
        const data = await response.json().catch(() => null);
        console.log("handleSubmit: record saved to DB successfully, response body:", data);
        console.log("handleSubmit: confirmation email should be triggered on backend for:", formData.customerEmail);
        setSubmitted(true);
        setFormErrors({});
      } else {
        const errText = await response.text().catch(() => "");
        console.log("handleSubmit: request FAILED, status:", response.status, "body:", errText);
        setFormErrors({ submit: "Something went wrong. Please call us directly." });
      }
    } catch (err) {
      console.error("handleSubmit: network/submission error:", err);
      setFormErrors({ submit: "Network error. Please call us directly." });
    } finally {
      setFormSubmitting(false);
      console.log("handleSubmit: finished, formSubmitting reset to false");
    }
  };

  const openPopup = (serviceTitle) => {
    console.log("openPopup: opening popup for service:", serviceTitle);
    setPopupData({ name: "", mobile: "", customerEmail: "", repairFor: serviceTitle, issueDetails: "" });
    setPopupErrors({});
    setPopupSubmitted(false);
    setPopupOpen(true);
  };

  const closePopup = () => {
    console.log("closePopup: popup closed");
    setPopupOpen(false);
  };

  const handlePopupChange = (field, value) => {
    setPopupData((prev) => ({ ...prev, [field]: value }));
    setPopupErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handlePopupSubmit = async () => {
    console.log("handlePopupSubmit: popup submit clicked", popupData);
    const errors = validateForm(popupData, true);
    setPopupErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.log("handlePopupSubmit: aborting submit due to validation errors", errors);
      return;
    }

    setPopupSubmitting(true);
    const payload = {
      customerName: popupData.name,
      phoneNumber: popupData.mobile,
      customerEmail: popupData.customerEmail,
      issueDescription: popupData.issueDetails
        ? `${popupData.repairFor} — ${popupData.issueDetails}`
        : popupData.repairFor,
    };
    console.log("handlePopupSubmit: sending POST to", SERVICE_REQUEST_API, "payload:", payload);

    try {
      const response = await fetch(SERVICE_REQUEST_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log("handlePopupSubmit: response status", response.status, response.ok);

      if (response.ok) {
        const data = await response.json().catch(() => null);
        console.log("handlePopupSubmit: record saved to DB successfully, response body:", data);
        console.log("handlePopupSubmit: confirmation email should be triggered on backend for:", popupData.customerEmail);
        setPopupSubmitted(true);
      } else {
        const errText = await response.text().catch(() => "");
        console.log("handlePopupSubmit: request FAILED, status:", response.status, "body:", errText);
        setPopupErrors({ submit: "Something went wrong. Please try calling us directly." });
      }
    } catch (err) {
      console.error("handlePopupSubmit: network/submission error:", err);
      setPopupErrors({ submit: "Network error. Please try calling us directly." });
    } finally {
      setPopupSubmitting(false);
      console.log("handlePopupSubmit: finished, popupSubmitting reset to false");
    }
  };

  // ---- Admin Functions ----
  const fetchRequests = async () => {
    console.log("fetchRequests: fetching requests, filterStatus =", filterStatus);
    setLoading(true);
    try {
      const url = filterStatus === "ALL"
        ? ADMIN_API
        : `${ADMIN_API}/status/${filterStatus}`;
      console.log("fetchRequests: GET", url);
      const res = await fetch(url);
      console.log("fetchRequests: response status", res.status, res.ok);
      const data = await res.json();
      console.log("fetchRequests: received", data.length, "records:", data);
      setRequests(data);
    } catch (err) {
      console.error("fetchRequests: fetch error:", err);
    } finally {
      setLoading(false);
      console.log("fetchRequests: finished, loading reset to false");
    }
  };

  const fetchStats = async () => {
    console.log("fetchStats: fetching stats from", `${ADMIN_API}/stats`);
    try {
      const res = await fetch(`${ADMIN_API}/stats`);
      console.log("fetchStats: response status", res.status, res.ok);
      const data = await res.json();
      console.log("fetchStats: stats received:", data);
      setStats(data);
    } catch (err) {
      console.error("fetchStats: stats error:", err);
    }
  };

  const updateStatus = async (id, newStatus) => {
    console.log("updateStatus: updating request id", id, "to status", newStatus);
    try {
      const res = await fetch(`${ADMIN_API}/${id}/status?status=${newStatus}`, { method: "PUT" });
      console.log("updateStatus: response status", res.status, res.ok);
      fetchRequests();
      fetchStats();
    } catch (err) {
      console.error("updateStatus: update error:", err);
    }
  };

  useEffect(() => {
    if (view === "admin") {
      console.log("useEffect: admin view active, loading requests + stats");
      fetchRequests();
      fetchStats();
    }
  }, [view, filterStatus]);

  useEffect(() => {
    if (!popupOpen) return;
    console.log("useEffect: popup opened, attaching escape-key listener");
    const onKeyDown = (e) => {
      if (e.key === "Escape") closePopup();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [popupOpen]);

  if (view === "admin") {
    return (
      <div style={{ fontFamily: "'Segoe UI', sans-serif", background: "#f7f8fa", minHeight: "100vh", padding: 24 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <h1 style={{ fontSize: 28, fontWeight: 900, color: "#1a1a2e" }}>Admin Dashboard</h1>
            <button onClick={() => setView("home")} style={{ background: "#22c55e", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
              ← Back to Site
            </button>
          </div>

          {/* Stats Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            {[
              { label: "Total", value: stats.total, color: "#3b82f6" },
              { label: "Pending", value: stats.pending, color: "#f59e0b" },
              { label: "Contacted", value: stats.contacted, color: "#8b5cf6" },
              { label: "Resolved", value: stats.resolved, color: "#22c55e" },
            ].map(s => (
              <div key={s.label} style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <div style={{ color: "#64748b", fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: s.color, marginTop: 4 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Filter */}
          <div style={{ background: "#fff", borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <select value={filterStatus} onChange={e => { console.log("Filter changed to:", e.target.value); setFilterStatus(e.target.value); }} style={{ padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 15 }}>
              <option value="ALL">All Requests</option>
              <option value="PENDING">Pending</option>
              <option value="CONTACTED">Contacted</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </div>

          {/* Table */}
          <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading...</div>
            ) : requests.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>No requests found</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#f1f5f9" }}>
                  <tr>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>ID</th>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>Customer</th>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>Phone</th>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>Email</th>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>Issue</th>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>Status</th>
                    <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#475569" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "14px 16px", fontSize: 14 }}>{r.id}</td>
                      <td style={{ padding: "14px 16px", fontSize: 14, fontWeight: 600 }}>{r.customerName}</td>
                      <td style={{ padding: "14px 16px", fontSize: 14 }}>{r.phoneNumber}</td>
                      <td style={{ padding: "14px 16px", fontSize: 14 }}>{r.customerEmail}</td>
                      <td style={{ padding: "14px 16px", fontSize: 14, maxWidth: 200 }}>{r.issueDescription}</td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{
                          padding: "4px 12px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          background: r.status === "PENDING" ? "#fef3c7" : r.status === "CONTACTED" ? "#ddd6fe" : "#d1fae5",
                          color: r.status === "PENDING" ? "#92400e" : r.status === "CONTACTED" ? "#5b21b6" : "#065f46"
                        }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <select
                          value={r.status}
                          onChange={e => updateStatus(r.id, e.target.value)}
                          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13 }}
                        >
                          <option value="PENDING">Pending</option>
                          <option value="CONTACTED">Contacted</option>
                          <option value="RESOLVED">Resolved</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", margin: 0, padding: 0, background: "#f7f8fa", color: "#1a1a2e" }}>
      {/* NAV */}
      <nav style={{ background: "#1a1a2e", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={logoImg} alt="CST Logo" style={{ width: 44, height: 44, borderRadius: "50%", border: "2px solid #22c55e", objectFit: "cover" }} />
          <div>
            <div style={{ color: "#22c55e", fontWeight: 800, fontSize: 16, lineHeight: 1 }}>COMPUTER SERVICE</div>
            <div style={{ color: "#ef4444", fontWeight: 700, fontSize: 13 }}>Technologies</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          {["Services", "Why Us", "Gallery", "Contact"].map(link => (
            <a key={link} href={`#${link.toLowerCase().replace(" ", "-")}`} style={{ color: "#e2e8f0", textDecoration: "none", fontSize: 14, fontWeight: 500, letterSpacing: "0.5px" }}
              onMouseEnter={e => e.target.style.color = "#22c55e"} onMouseLeave={e => e.target.style.color = "#e2e8f0"}>
              {link}
            </a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => { console.log("Navigating to admin view"); setView("admin"); }} style={{ background: "#8b5cf6", color: "#fff", padding: "8px 16px", borderRadius: 8, border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            Admin
          </button>
          <a href="tel:8125399952" style={{ background: "#22c55e", color: "#fff", padding: "8px 16px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
            📞 Call Now
          </a>
        </div>
      </nav>

      {/* CTA BAR */}
      <div style={{ background: "#1a1a2e", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
        <a href="tel:8125399952" style={{ background: "#22c55e", color: "#fff", padding: "10px 24px", borderRadius: 8, textDecoration: "none", fontWeight: 800, fontSize: 16 }}>
          📞 8125399952
        </a>
        <a href="#contact" style={{ background: "transparent", color: "#fff", padding: "10px 20px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 15, border: "2px solid #ffffff55" }}>
          Get Callback →
        </a>
      </div>

      {/* SERVICES */}
      <section id="services" style={{ padding: "60px 24px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ color: "#22c55e", fontWeight: 700, fontSize: 12, letterSpacing: 4, textTransform: "uppercase" }}>What We Repair</div>
          <h2 style={{ fontSize: 34, fontWeight: 900, margin: "8px 0 0" }}>Our Services</h2>
          <div style={{ width: 48, height: 4, background: "#22c55e", margin: "12px auto 0", borderRadius: 2 }} />
          <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 12 }}>Tap any service below to request a repair</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
          {services.map(s => (
            <div key={s.title}
              role="button"
              tabIndex={0}
              onClick={() => openPopup(s.title)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openPopup(s.title); }}
              style={{ background: "#fff", borderRadius: 14, padding: "28px 24px", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", borderTop: "4px solid #22c55e", transition: "transform 0.2s", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-4px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{s.icon}</div>
              <h3 style={{ margin: "0 8px", fontSize: 18, fontWeight: 800, color: "#1a1a2e" }}>{s.title}</h3>
              <p style={{ margin: 0, color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>{s.desc}</p>
              <div style={{ marginTop: 14, color: "#22c55e", fontWeight: 700, fontSize: 13 }}>Request this repair →</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section id="why-us" style={{ background: "#1a1a2e", padding: "60px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ color: "#22c55e", fontWeight: 700, fontSize: 12, letterSpacing: 4, textTransform: "uppercase" }}>Reasons to Choose Us</div>
            <h2 style={{ fontSize: 34, fontWeight: 900, color: "#fff", margin: "8px 0 0" }}>Why CST?</h2>
            <div style={{ width: 48, height: 4, background: "#ef4444", margin: "12px auto 0", borderRadius: 2 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 20 }}>
            {whyUs.map(w => (
              <div key={w.point} style={{ background: "#0f172a", borderRadius: 12, padding: "24px 20px", textAlign: "center", border: "1px solid #334155" }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{w.icon}</div>
                <div style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 15 }}>{w.point}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" style={{ padding: "60px 24px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ color: "#22c55e", fontWeight: 700, fontSize: 12, letterSpacing: 4, textTransform: "uppercase" }}>From Our Workshop</div>
          <h2 style={{ fontSize: 34, fontWeight: 900, margin: "8px 0 0" }}>Gallery</h2>
          <div style={{ width: 48, height: 4, background: "#22c55e", margin: "12px auto 0", borderRadius: 2 }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <img src={gallery1Img} alt="CST Shop" style={{ width: "100%", borderRadius: 14, objectFit: "cover", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }} />
          <img src={gallery2Img} alt="Laptop spare parts" style={{ width: "100%", borderRadius: 14, objectFit: "cover", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }} />
        </div>
        <p style={{ textAlign: "center", marginTop: 16, color: "#64748b", fontSize: 14 }}>MIG 18, Cellar, Phase 1, Dharamareddy Colony, KPHB, Hyderabad 500085 &nbsp;·&nbsp; cst.instant2020@gmail.com</p>
      </section>

      {/* CONTACT */}
      <section id="contact" style={{ background: "#f1f5f9", padding: "60px 24px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto", background: "#fff", borderRadius: 18, padding: "40px 36px", boxShadow: "0 4px 32px rgba(0,0,0,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ color: "#22c55e", fontWeight: 700, fontSize: 12, letterSpacing: 4, textTransform: "uppercase" }}>Request a Callback</div>
            <h2 style={{ fontSize: 28, fontWeight: 900, margin: "8px 0 0" }}>Describe Your Issue</h2>
            <p style={{ color: "#64748b", fontSize: 14, marginTop: 8 }}>We'll call you back within the hour.</p>
          </div>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 56 }}>✅</div>
              <h3 style={{ color: "#22c55e", marginTop: 12 }}>Thanks, {formData.name}!</h3>
              <p style={{ color: "#64748b" }}>We'll call you at <strong>{formData.phone}</strong> shortly.</p>
              <a href="tel:8125399952" style={{ display: "inline-block", marginTop: 12, background: "#22c55e", color: "#fff", padding: "10px 24px", borderRadius: 8, textDecoration: "none", fontWeight: 700 }}>Or call us now: 8125399952</a>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <input placeholder="Your Name" value={formData.name} onChange={e => handleFormChange("name", e.target.value)}
                  style={inputStyle(!!formErrors.name)} aria-invalid={!!formErrors.name} />
                {formErrors.name && <div style={errorTextStyle}>{formErrors.name}</div>}
              </div>
              <div>
                <input placeholder="Phone Number" value={formData.phone}
                  onChange={e => handleFormChange("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  style={inputStyle(!!formErrors.phone)} aria-invalid={!!formErrors.phone} />
                {formErrors.phone && <div style={errorTextStyle}>{formErrors.phone}</div>}
              </div>
              <div>
                <input placeholder="Email" value={formData.customerEmail} onChange={e => handleFormChange("customerEmail", e.target.value)}
                  style={inputStyle(!!formErrors.customerEmail)} aria-invalid={!!formErrors.customerEmail} />
                {formErrors.customerEmail && <div style={errorTextStyle}>{formErrors.customerEmail}</div>}
              </div>
              <div>
                <textarea placeholder="Describe your problem (e.g. laptop screen broken, desktop not starting...)" value={formData.issue}
                  onChange={e => handleFormChange("issue", e.target.value)} rows={4}
                  style={{ ...inputStyle(!!formErrors.issue), resize: "vertical" }} aria-invalid={!!formErrors.issue} />
                {formErrors.issue && <div style={errorTextStyle}>{formErrors.issue}</div>}
              </div>
              {formErrors.submit && <div style={errorTextStyle}>{formErrors.submit}</div>}
              <button onClick={handleSubmit} disabled={formSubmitting}
                style={{ background: formSubmitting ? "#94d3ac" : "#22c55e", color: "#fff", border: "none", padding: "14px", borderRadius: 10, fontSize: 16, fontWeight: 800, cursor: formSubmitting ? "not-allowed" : "pointer" }}>
                {formSubmitting ? "Submitting..." : "📲 Request Callback"}
              </button>
              <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13 }}>or call directly &nbsp;
                <a href="tel:8125399952" style={{ color: "#22c55e", fontWeight: 700, textDecoration: "none" }}>8125399952</a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: "#0f172a", padding: "28px 24px", textAlign: "center" }}>
        <img src={logoImg} alt="CST" style={{ width: 40, height: 40, borderRadius: "50%", border: "2px solid #22c55e", objectFit: "cover", marginBottom: 10 }} />
        <div style={{ color: "#22c55e", fontWeight: 800, fontSize: 15 }}>Computer Service Technologies</div>
        <div style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>MIG 18, Cellar, Phase 1, Dharamareddy Colony, KPHB, Hyderabad 500085</div>
        <div style={{ color: "#64748b", fontSize: 13 }}>cst.instant2020@gmail.com &nbsp;·&nbsp; 8125399952 &nbsp;·&nbsp; 9908771145</div>
        <div style={{ color: "#334155", fontSize: 12, marginTop: 12 }}>© 2026 Computer Service Technologies. All rights reserved.</div>
      </footer>

      {/* FLOATING CALL BUTTON */}
      <a href="tel:8125399952" style={{ position: "fixed", bottom: 24, right: 24, background: "#22c55e", color: "#fff", width: 60, height: 60, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, textDecoration: "none", boxShadow: "0 4px 20px rgba(34,197,94,0.5)", zIndex: 999 }}
        title="Call CST Now">
        📞
      </a>

      {/* POPUP MODAL */}
      {popupOpen && (
        <div
          onClick={closePopup}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 18, padding: "32px 28px",
              maxWidth: 440, width: "100%", maxHeight: "90vh", overflowY: "auto",
              boxShadow: "0 12px 48px rgba(0,0,0,0.25)", position: "relative",
            }}
          >
            <button
              onClick={closePopup}
              aria-label="Close"
              style={{
                position: "absolute", top: 14, right: 14, width: 32, height: 32,
                borderRadius: "50%", border: "none", background: "#f1f5f9",
                color: "#64748b", fontSize: 18, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              ✕
            </button>

            {popupSubmitted ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 52 }}>✅</div>
                <h3 style={{ color: "#22c55e", marginTop: 10, marginBottom: 6 }}>Request Received, {popupData.name}!</h3>
                <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>
                  We've noted your <strong>{popupData.repairFor}</strong> request and will call <strong>{popupData.mobile}</strong> shortly.
                </p>
                <button
                  onClick={closePopup}
                  style={{ marginTop: 16, background: "#22c55e", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer" }}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 22 }}>
                  <div style={{ color: "#22c55e", fontWeight: 700, fontSize: 12, letterSpacing: 3, textTransform: "uppercase" }}>Book a Repair</div>
                  <h2 style={{ fontSize: 22, fontWeight: 900, margin: "6px 0 0" }}>{popupData.repairFor || "Request Service"}</h2>
                  <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 6 }}>Fill in your details and we'll call you back.</p>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                  <div>
                    <label style={labelStyle} htmlFor="popup-name">Customer Name</label>
                    <input
                      id="popup-name"
                      type="text"
                      placeholder="e.g. Rajesh"
                      value={popupData.name}
                      onChange={(e) => handlePopupChange("name", e.target.value)}
                      style={inputStyle(!!popupErrors.name)}
                      aria-invalid={!!popupErrors.name}
                    />
                    {popupErrors.name && <div style={errorTextStyle}>{popupErrors.name}</div>}
                  </div>

                  <div>
                    <label style={labelStyle} htmlFor="popup-mobile">Mobile Number</label>
                    <input
                      id="popup-mobile"
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit mobile number"
                      value={popupData.mobile}
                      maxLength={10}
                      onChange={(e) => handlePopupChange("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      style={inputStyle(!!popupErrors.phone)}
                      aria-invalid={!!popupErrors.phone}
                    />
                    {popupErrors.phone && <div style={errorTextStyle}>{popupErrors.phone}</div>}
                  </div>

                  <div>
                    <label style={labelStyle} htmlFor="popup-email">Customer Email</label>
                    <input
                      id="popup-email"
                      type="email"
                      placeholder="e.g. rajesh@gmail.com"
                      value={popupData.customerEmail}
                      onChange={(e) => handlePopupChange("customerEmail", e.target.value)}
                      style={inputStyle(!!popupErrors.customerEmail)}
                      aria-invalid={!!popupErrors.customerEmail}
                    />
                    {popupErrors.customerEmail && <div style={errorTextStyle}>{popupErrors.customerEmail}</div>}
                  </div>

                  <div>
                    <label style={labelStyle} htmlFor="popup-repair">Repair For</label>
                    <select
                      id="popup-repair"
                      value={popupData.repairFor}
                      onChange={(e) => handlePopupChange("repairFor", e.target.value)}
                      style={inputStyle(!!popupErrors.repairFor)}
                      aria-invalid={!!popupErrors.repairFor}
                    >
                      <option value="">Select a service</option>
                      {services.map((s) => (
                        <option key={s.title} value={s.title}>{s.icon} {s.title}</option>
                      ))}
                    </select>
                    {popupErrors.repairFor && <div style={errorTextStyle}>{popupErrors.repairFor}</div>}
                  </div>

                  <div>
                    <label style={labelStyle} htmlFor="popup-issue">Describe the Issue (optional)</label>
                    <textarea
                      id="popup-issue"
                      placeholder="e.g. screen flickering, not turning on, battery drains fast..."
                      value={popupData.issueDetails}
                      onChange={(e) => handlePopupChange("issueDetails", e.target.value)}
                      rows={3}
                      style={{ ...inputStyle(false), resize: "vertical" }}
                    />
                  </div>

                  {popupErrors.submit && <div style={errorTextStyle}>{popupErrors.submit}</div>}

                  <button
                    onClick={handlePopupSubmit}
                    disabled={popupSubmitting}
                    style={{
                      background: popupSubmitting ? "#94d3ac" : "#22c55e",
                      color: "#fff", border: "none", padding: "14px", borderRadius: 10,
                      fontSize: 16, fontWeight: 800,
                      cursor: popupSubmitting ? "not-allowed" : "pointer", marginTop: 4,
                    }}
                  >
                    {popupSubmitting ? "Submitting..." : "📲 Submit"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}